import { ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from './sortable-list-modifiers';
import Box from '@mui/material/Box';

/** Props to spread on the element that starts a drag (the drag handle). */
export interface SortableHandleProps {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  setActivatorNodeRef: ReturnType<typeof useSortable>['setActivatorNodeRef'];
}

interface SortableListProps<T> {
  items: T[];
  getId: (item: T) => string;
  /** Called once when an item is dropped at a new position. */
  onMove: (fromIndex: number, toIndex: number) => void;
  renderItem: (
    item: T,
    index: number,
    handle: SortableHandleProps,
    isDragging: boolean
  ) => ReactNode;
  /** Notified when a drag starts and when it ends or is cancelled (Esc). */
  onDragStateChange?: (isDragging: boolean) => void;
  /** Vertical gap between items, in theme spacing units. */
  gap?: number;
}

const SNAP_TRANSITION = { duration: 150, easing: 'ease' };

/** The floating copy under the pointer isn't itself draggable, so its handle is inert. */
const OVERLAY_HANDLE: SortableHandleProps = {
  attributes: {} as SortableHandleProps['attributes'],
  listeners: undefined,
  setActivatorNodeRef: () => {},
};

function SortableItem({
  id,
  children,
}: {
  id: string;
  children: (handle: SortableHandleProps, isDragging: boolean) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, transition: SNAP_TRANSITION });

  return (
    <Box
      ref={setNodeRef}
      data-testid={`sortable-item-${id}`}
      sx={{
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
      }}
    >
      {isDragging ? (
        // While dragging, the item's slot becomes a dashed placeholder at the drop position; the
        // item itself floats under the pointer (see DragOverlay). The hidden content keeps the
        // placeholder the same height as the item.
        <Box
          data-testid="sortable-placeholder"
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Box sx={{ visibility: 'hidden' }}>
            {children({ attributes, listeners, setActivatorNodeRef }, true)}
          </Box>
        </Box>
      ) : (
        children({ attributes, listeners, setActivatorNodeRef }, false)
      )}
    </Box>
  );
}

/**
 * A vertical list that can be reordered by dragging each item's handle. Items move only within
 * this list; nest another SortableList for child items (they get their own drag context, so child
 * items can't be dropped into the parent list). While dragging, a floating copy of the item follows
 * the pointer (vertically only) and a dashed placeholder marks the drop position. Pressing Esc
 * cancels the drag, and the list auto-scrolls near the edges of the scroll area.
 */
export default function SortableList<T>({
  items,
  getId,
  onMove,
  renderItem,
  onDragStateChange,
  gap = 1,
}: SortableListProps<T>) {
  const sensors = useSensors(
    // A small distance keeps clicks on the handle from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const ids = items.map(getId);
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIndex = activeId === null ? -1 : ids.indexOf(activeId);
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    onDragStateChange?.(true);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    onDragStateChange?.(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    onDragStateChange?.(false);
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const fromIndex = ids.indexOf(String(active.id));
    const toIndex = ids.indexOf(String(over.id));
    if (fromIndex >= 0 && toIndex >= 0) {
      onMove(fromIndex, toIndex);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
          {items.map((item, index) => (
            <SortableItem key={getId(item)} id={getId(item)}>
              {(handle, isDragging) =>
                renderItem(item, index, handle, isDragging)
              }
            </SortableItem>
          ))}
        </Box>
      </SortableContext>
      {/* Rendered in the body so a parent's overflow (e.g. a collapsed card) can't clip it. */}
      {createPortal(
        <DragOverlay
          modifiers={[restrictToVerticalAxis]}
          dropAnimation={SNAP_TRANSITION}
        >
          {activeItem ? (
            <Box
              data-testid="sortable-drag-overlay"
              sx={{ boxShadow: 6, borderRadius: 1, cursor: 'grabbing' }}
            >
              {renderItem(activeItem, activeIndex, OVERLAY_HANDLE, true)}
            </Box>
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}
